/**
 * Purpose:
 *   Manages expanded sidebar menu sections for active and manual states.
 *
 * Notes:
 *   Clears optimistic navigation state after the pathname actually changes so stale menu
 *   selections do not reappear when the user later returns to the original route.
 */

'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { MenuItemConfig } from '@/types/menu';
import { findActiveParentItemId } from '../(utils)/matchMenuItemPath';

interface IUseMenuExpansionProps {
  items: MenuItemConfig[];
  /** Menu match route used to determine the active and expanded item. */
  pathname: string;
  /** Real browser pathname used for optimistic navigation bookkeeping. */
  navigationPathname?: string;
}

interface IManualExpansionState {
  itemId: string | null;
  pathname: string;
}

interface IManualCollapsedActiveState {
  itemId: string | null;
  pathname: string;
}

interface IPendingNavigationState {
  targetPathname: string;
  fromPathname: string;
}

export interface IUseMenuExpansionReturn {
  menuPathname: string;
  expandedItems: Set<string>;
  toggleExpanded: (itemId: string) => void;
  handleNavigate: (href: string) => void;
}

export function useMenuExpansion({
  items,
  pathname,
  navigationPathname,
}: IUseMenuExpansionProps): IUseMenuExpansionReturn {
  const resolvedNavigationPathname = navigationPathname ?? pathname;
  const [pendingNavigation, setPendingNavigation] =
    useState<IPendingNavigationState | null>(null);
  const [manualExpansion, setManualExpansion] = useState<IManualExpansionState>({
    itemId: null,
    pathname: '',
  });
  const [manualCollapsedActive, setManualCollapsedActive] =
    useState<IManualCollapsedActiveState>({
      itemId: null,
      pathname: '',
    });

  const menuPathname =
    pendingNavigation?.fromPathname === resolvedNavigationPathname
      ? pendingNavigation.targetPathname
      : pathname;

  useEffect(() => {
    if (!pendingNavigation) return;
    if (resolvedNavigationPathname === pendingNavigation.fromPathname) return;

    queueMicrotask(() => {
      setPendingNavigation((current) => {
        if (!current) return null;
        if (current.fromPathname !== pendingNavigation.fromPathname) return current;
        if (current.targetPathname !== pendingNavigation.targetPathname) return current;
        return null;
      });
    });
  }, [resolvedNavigationPathname, pendingNavigation]);

  const activeParentItemId = useMemo(
    () => findActiveParentItemId(items, menuPathname),
    [items, menuPathname]
  );
  const manualExpandedItemId =
    manualExpansion.pathname === menuPathname ? manualExpansion.itemId : null;
  const isActiveParentManuallyCollapsed =
    manualCollapsedActive.pathname === menuPathname &&
    manualCollapsedActive.itemId === activeParentItemId;

  const expandedItems = useMemo(() => {
    return new Set(
      [
        isActiveParentManuallyCollapsed ? null : activeParentItemId,
        manualExpandedItemId,
      ].filter(Boolean) as string[]
    );
  }, [
    activeParentItemId,
    isActiveParentManuallyCollapsed,
    manualExpandedItemId,
  ]);

  const toggleExpanded = useCallback(
    (itemId: string) => {
      if (itemId === activeParentItemId) {
        setManualCollapsedActive((prev) => {
          const shouldOpen =
            prev.pathname === menuPathname && prev.itemId === itemId;
          return { itemId: shouldOpen ? null : itemId, pathname: menuPathname };
        });
        setManualExpansion((prev) => {
          const shouldClear =
            prev.pathname === menuPathname && prev.itemId === itemId;
          return shouldClear ? { itemId: null, pathname: menuPathname } : prev;
        });
        return;
      }

      setManualExpansion((prev) => {
        const shouldClose =
          prev.pathname === menuPathname && prev.itemId === itemId;
        return { itemId: shouldClose ? null : itemId, pathname: menuPathname };
      });
    },
    [activeParentItemId, menuPathname]
  );

  const handleNavigate = useCallback(
    (href: string) => {
      setManualExpansion({ itemId: null, pathname: href });
      setManualCollapsedActive({ itemId: null, pathname: href });
      setPendingNavigation({
        targetPathname: href,
        fromPathname: resolvedNavigationPathname,
      });
    },
    [resolvedNavigationPathname]
  );

  return useMemo(
    () => ({ menuPathname, expandedItems, toggleExpanded, handleNavigate }),
    [expandedItems, handleNavigate, menuPathname, toggleExpanded]
  );
}
